import React, { useState, useCallback } from 'react';
import { AppContext } from './AppContext';
import AuthInitializer from './AuthInitializer';
import UserInitializer from './UserInitializer';
import { User } from '../api/user';
import firebase from 'firebase/compat/app';
import { useAlertContext } from './AlertContext';

type Props = {
  children: React.ReactNode;
};

type TokenInfo = {
  impersonationActor: string;
  isAdmin: boolean;
  isViewer: boolean;
  clock: string | null;
};

const AppContextProvider: React.FC<Props> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [authUser, setAuthUser] = useState<firebase.User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isViewer, setIsViewer] = useState(false);
  const { alert, showAlert } = useAlertContext();
  const [hasAgreedToContract, setHasAgreedToContract] =
    useState<boolean>(false);
  const [clock, setClock] = useState<string | null>(null);

  const [impersonationActor, setImpersonationActor] = useState<
    string | undefined
  >();

  const handleSetImpersonationActor = useCallback((actor: string) => {
    setImpersonationActor(actor);
  }, []);

  const getInfoFromToken = async (
    user: firebase.User | null,
    onValidateInfo: (tokenInfo: TokenInfo) => void,
    forceRefresh = false,
  ): Promise<void> => {
    try {
      if (!user) {
        onValidateInfo({
          impersonationActor: '',
          isAdmin: false,
          isViewer: false,
          clock: null,
        });
        return;
      }

      const idTokenResult = await user.getIdTokenResult(forceRefresh);
      const impersonationActor = (idTokenResult.claims?.actor as string) || '';
      const isAdmin = idTokenResult.claims?.role === 'admin';
      const isViewer = idTokenResult.claims?.role === 'viewer';
      const clocks = (idTokenResult.claims?.clocks as string[]) || [];
      const clock = clocks.length > 0 ? clocks[0] : null;
      onValidateInfo({ impersonationActor, isAdmin, isViewer, clock });
    } catch (error) {
      console.error('Error validating admin role:', error);
      throw error;
    }
  };

  const handleValidation = useCallback(
    (tokenInfo: TokenInfo) => {
      setIsAdmin(tokenInfo.isAdmin);
      setIsViewer(tokenInfo.isViewer);
      handleSetImpersonationActor(tokenInfo.impersonationActor);
      setClock(tokenInfo.clock);
    },
    [handleSetImpersonationActor],
  );

  const handleSetAuthUser = (user: firebase.User | null) => {
    setAuthUser(user);
    getInfoFromToken(user, handleValidation);
  };

  const refreshToken = useCallback(async () => {
    if (authUser) {
      await getInfoFromToken(authUser, handleValidation, true);
    }
  }, [authUser, handleValidation]);

  return (
    <AppContext.Provider
      value={{
        user: user!,
        setUser,
        authUser: authUser!,
        setAuthUser: handleSetAuthUser,
        isAdmin,
        isViewer,
        impersonationActor: impersonationActor,
        alert,
        showAlert,
        hasAgreedToContract,
        setHasAgreedToContract,
        clock,
        refreshToken,
      }}
    >
      <AuthInitializer setAuthUser={handleSetAuthUser} isAdmin={isAdmin}>
        {authUser && (
          <UserInitializer
            authUser={authUser}
            setUser={setUser}
            isAdmin={isAdmin}
            setHasAgreedToContract={setHasAgreedToContract}
          >
            {children}
          </UserInitializer>
        )}
      </AuthInitializer>
    </AppContext.Provider>
  );
};

export default AppContextProvider;
